import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_METHODS } from '@makila/fiscal';
import { emailListInput } from '@makila/shared';
import { Building2, FileDown, FilePlus2, Mail, MoreHorizontal, Pencil, Phone, Receipt, Trash2, User, UserPlus, Users, Wallet } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Badge, Button, Card, Checkbox, Chips, Dropdown, Empty, errorText, Field, fieldErrors, Input, KeyValues, Loading, Modal, PageHeader, Pagination, SearchInput,
  Select, StatCard, StatusBadge, Table, Tabs, Td, Textarea, Th, Toolbar, Tr, useConfirm, useToast,
} from '../components/ui';
import { del, download, get, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, dateTime, docLabel, monthStart, mt, today } from '../lib/format';
import { PROVINCES } from '../lib/lists';

type C = Record<string, any>;
const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const n = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));

type FormTab = 'id' | 'contactos' | 'moradas' | 'comercial' | 'notas';

/** Ficha completa do cliente, em separadores. */
export function CustomerForm({ initial, onSaved, onCancel }: { initial?: C; onSaved: (c: C) => void; onCancel?: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  // O limite de crédito controla a emissão: só quem gere a conta corrente o altera (a API recusa os outros).
  const creditLocked = !can('accounts:edit');
  const creditHint = creditLocked ? 'Só quem gere a conta corrente altera' : 'Vazio = sem limite';
  const [tab, setTab] = useState<FormTab>('id');
  const [f, setF] = useState({
    customerType: s(initial?.customer_type) || 'company', code: s(initial?.code), name: s(initial?.name), tradeName: s(initial?.trade_name), nuit: s(initial?.nuit),
    email: s(initial?.email), billingEmail: s(initial?.billing_email), phone: s(initial?.phone), mobile: s(initial?.mobile), whatsapp: s(initial?.whatsapp), website: s(initial?.website),
    contactName: s(initial?.contact_name), contactRole: s(initial?.contact_role),
    address: s(initial?.address), city: s(initial?.city), province: s(initial?.province), postalCode: s(initial?.postal_code),
    deliveryAddress: s(initial?.delivery_address), deliveryCity: s(initial?.delivery_city),
    paymentTermsDays: s(initial?.payment_terms_days), defaultPaymentMethod: s(initial?.default_payment_method), defaultDiscountPct: n(initial?.default_discount_pct),
    creditLimit: n(initial?.credit_limit), creditDaysLimit: s(initial?.credit_days_limit), withholdingPct: n(initial?.withholding_pct), withholdingType: s(initial?.withholding_type) || 'IRPC',
    isPublicEntity: !!initial?.is_public_entity, tags: ((initial?.tags ?? []) as string[]).join(', ') as string, notes: s(initial?.notes), active: initial?.active ?? true,
    autoReminders: initial?.auto_reminders ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const err = (k: string) => errors[k];
  const tabOf: Record<string, FormTab> = {
    name: 'id', nuit: 'id', code: 'id', tradeName: 'id', email: 'contactos', billingEmail: 'contactos', website: 'contactos',
    address: 'moradas', city: 'moradas', paymentTermsDays: 'comercial', creditLimit: 'comercial', withholdingPct: 'comercial', defaultDiscountPct: 'comercial', creditDaysLimit: 'comercial',
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErrors({});
    const body = {
      ...f, code: f.code || null, nuit: f.nuit || null, tradeName: f.tradeName || null, isFinalConsumer: !!initial?.is_final_consumer,
      paymentTermsDays: f.paymentTermsDays === '' ? null : f.paymentTermsDays, defaultPaymentMethod: f.defaultPaymentMethod || null,
      defaultDiscountPct: f.defaultDiscountPct || null, creditLimit: f.creditLimit || null, creditDaysLimit: f.creditDaysLimit === '' ? null : f.creditDaysLimit,
      withholdingPct: f.withholdingPct || null, withholdingType: f.withholdingPct ? f.withholdingType : null,
      tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    try {
      const r = initial ? await patch(`/customers/${initial.id}`, body) : await post('/customers', body);
      toast(initial ? 'Cliente actualizado' : 'Cliente criado');
      onSaved(r.customer);
    } catch (ex) {
      const fe = fieldErrors(ex);
      setErrors(fe);
      const first = Object.keys(fe)[0];
      if (first && tabOf[first]) setTab(tabOf[first]);
      toast(errorText(ex), 'error');
    } finally { setBusy(false); }
  }

  const company = f.customerType === 'company';
  return (
    <form onSubmit={submit} noValidate>
      <Tabs value={tab} onChange={setTab} className="-mx-6 -mt-5 mb-5 px-6" tabs={[
        { id: 'id', label: 'Identificação' }, { id: 'contactos', label: 'Contactos' }, { id: 'moradas', label: 'Moradas' },
        { id: 'comercial', label: 'Condições comerciais' }, { id: 'notas', label: 'Notas' },
      ]} />
      <div className="min-h-[300px]">
        {tab === 'id' && (
          <div className="space-y-4">
            <Chips value={f.customerType} onChange={(v) => setF({ ...f, customerType: v })} options={[{ id: 'company', label: 'Empresa / instituição' }, { id: 'individual', label: 'Particular' }]} />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={company ? 'Firma / denominação social' : 'Nome completo'} required className="sm:col-span-2" error={err('name')}><Input autoFocus value={f.name} onChange={set('name')} /></Field>
              <Field label="NUIT" error={err('nuit')} hint={company ? 'Obrigatório para emitir Facturas' : 'Opcional para particulares'}><Input inputMode="numeric" placeholder="9 dígitos" value={f.nuit} onChange={set('nuit')} /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {company && <Field label="Nome comercial" className="sm:col-span-2"><Input value={f.tradeName} onChange={set('tradeName')} /></Field>}
              <Field label="Código" hint="Vazio = automático" error={err('code')}><Input value={f.code} onChange={set('code')} /></Field>
            </div>
            <div className="space-y-3 rounded-xl border border-[var(--border)] p-4">
              <Checkbox label="Entidade pública" hint="Imprime o NIB para pagamento ao Estado (CIVA art. 27 n.º 5 f)" checked={f.isPublicEntity} onChange={(v) => setF({ ...f, isPublicEntity: v })} />
              {initial && <Checkbox label="Cliente activo" hint="Clientes inactivos não aparecem na pesquisa ao facturar" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />}
            </div>
          </div>
        )}
        {tab === 'contactos' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email geral" error={err('email')}><Input type="email" value={f.email} onChange={set('email')} /></Field>
            <Field label="Email para envio de documentos" error={err('billingEmail')} hint="Se vazio, usa o email geral"><Input type="email" value={f.billingEmail} onChange={set('billingEmail')} /></Field>
            <Field label="Telefone"><Input type="tel" value={f.phone} onChange={set('phone')} placeholder="+258 21 …" /></Field>
            <Field label="Telemóvel"><Input type="tel" value={f.mobile} onChange={set('mobile')} placeholder="+258 84 …" /></Field>
            <Field label="WhatsApp"><Input type="tel" value={f.whatsapp} onChange={set('whatsapp')} /></Field>
            <Field label="Website"><Input value={f.website} onChange={set('website')} placeholder="empresa.co.mz" /></Field>
            <Field label="Pessoa de contacto"><Input value={f.contactName} onChange={set('contactName')} /></Field>
            <Field label="Cargo / função"><Input value={f.contactRole} onChange={set('contactRole')} placeholder="Ex.: Compras, Contabilidade" /></Field>
          </div>
        )}
        {tab === 'moradas' && (
          <div className="space-y-5">
            <div>
              <h3 className="mb-3 text-sm font-semibold">Morada de facturação</h3>
              <div className="grid gap-4 sm:grid-cols-4">
                <Field label="Morada" className="sm:col-span-4" error={err('address')}><Input value={f.address} onChange={set('address')} placeholder="Avenida, rua, número, bairro" /></Field>
                <Field label="Cidade" className="sm:col-span-2"><Input value={f.city} onChange={set('city')} /></Field>
                <Field label="Província">
                  <Select value={f.province} onChange={set('province')}>
                    <option value="">—</option>
                    {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </Field>
                <Field label="Código postal"><Input value={f.postalCode} onChange={set('postalCode')} /></Field>
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-semibold">Morada de entrega <span className="font-normal text-[var(--muted)]">(se diferente)</span></h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Morada" className="sm:col-span-2"><Input value={f.deliveryAddress} onChange={set('deliveryAddress')} /></Field>
                <Field label="Cidade"><Input value={f.deliveryCity} onChange={set('deliveryCity')} /></Field>
              </div>
            </div>
          </div>
        )}
        {tab === 'comercial' && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Prazo de pagamento (dias)" hint="Vazio = prazo da empresa" error={err('paymentTermsDays')}><Input inputMode="numeric" value={f.paymentTermsDays} onChange={set('paymentTermsDays')} /></Field>
              <Field label="Meio de pagamento habitual">
                <Select value={f.defaultPaymentMethod} onChange={set('defaultPaymentMethod')}>
                  <option value="">—</option>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
              </Field>
              <Field label="Desconto habitual %" error={err('defaultDiscountPct')} hint="Aplicado por omissão às linhas"><Input inputMode="decimal" value={f.defaultDiscountPct} onChange={set('defaultDiscountPct')} /></Field>
            </div>
            <div className="rounded-xl border border-[var(--border)] p-4">
              <h3 className="text-sm font-semibold">Controlo de crédito</h3>
              <p className="mb-3 text-xs text-[var(--muted)]">Ao ultrapassar um limite, a emissão de facturas fica bloqueada salvo autorização (registada na auditoria).</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Limite de crédito (MT)" error={err('creditLimit')} hint={creditHint}><Input inputMode="decimal" value={f.creditLimit} onChange={set('creditLimit')} disabled={creditLocked} /></Field>
                <Field label="Dias de atraso tolerados" error={err('creditDaysLimit')} hint={creditHint}><Input inputMode="numeric" value={f.creditDaysLimit} onChange={set('creditDaysLimit')} disabled={creditLocked} /></Field>
              </div>
            </div>
            <div className="rounded-xl border border-[var(--border)] p-4">
              <h3 className="text-sm font-semibold">Retenção na fonte</h3>
              <p className="mb-3 text-xs text-[var(--muted)]">Para clientes que retêm IRPC/IRPS sobre serviços (ex.: 10%). O documento mostra o valor retido e o líquido a receber.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Taxa %" error={err('withholdingPct')}><Input inputMode="decimal" value={f.withholdingPct} onChange={set('withholdingPct')} placeholder="0" /></Field>
                <Field label="Imposto"><Select value={f.withholdingType} onChange={set('withholdingType')}><option value="IRPC">IRPC</option><option value="IRPS">IRPS</option></Select></Field>
              </div>
            </div>
          </div>
        )}
        {tab === 'notas' && (
          <div className="space-y-4">
            <Field label="Etiquetas" hint="Separadas por vírgulas — ex.: revenda, hotelaria"><Input value={f.tags} onChange={set('tags')} /></Field>
            <Checkbox label="Lembretes automáticos" hint="Lembretes de pagamento e seguimento de pró-formas, quando ligados em Definições → Automação" checked={f.autoReminders} onChange={(v) => setF({ ...f, autoReminders: v })} />
            <Field label="Notas internas" hint="Nunca são impressas nos documentos"><Textarea rows={6} value={f.notes} onChange={set('notes')} /></Field>
          </div>
        )}
      </div>
      <div className="-mx-6 mt-5 -mb-5 flex justify-end gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-6 py-3.5">
        {onCancel && <Button variant="secondary" onClick={onCancel}>Cancelar</Button>}
        <Button type="submit" loading={busy}>{initial ? 'Guardar alterações' : 'Criar cliente'}</Button>
      </div>
    </form>
  );
}

export function CustomersPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const creating = sp.get('novo') === '1';
  const setCreating = (v: boolean) => { const p = new URLSearchParams(sp); if (v) p.set('novo', '1'); else p.delete('novo'); setSp(p, { replace: true }); };
  const list = useQuery({ queryKey: ['customers', q, page, filter], queryFn: () => get<{ items: C[]; total: number }>(`/customers?page=${page}&pageSize=25&q=${encodeURIComponent(q)}&active=${filter === 'all' ? 'all' : ''}`) });

  return (
    <>
      <PageHeader eyebrow="Clientes" title="Clientes" subtitle="Fichas, condições comerciais e crédito"
        actions={can('customers:create') && <Button icon={<UserPlus className="size-4" />} onClick={() => setCreating(true)}>Novo cliente</Button>} />
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-72" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Nome, NUIT, código ou email" />
          <Chips value={filter} onChange={(v) => { setFilter(v); setPage(1); }} options={[{ id: 'active', label: 'Activos' }, { id: 'all', label: 'Todos' }]} />
        </Toolbar>
        {list.isLoading ? <div className="p-5"><Loading /></div> : !list.data?.items.length ? (
          <Empty icon={<Users />} title={q ? 'Nenhum cliente encontrado' : 'Ainda não tem clientes'} action={can('customers:create') && !q && <Button icon={<UserPlus className="size-4" />} onClick={() => setCreating(true)}>Criar o primeiro cliente</Button>}>
            {q ? 'Experimente outro termo de pesquisa.' : 'Crie os seus clientes para facturar em segundos.'}
          </Empty>
        ) : (
          <>
            <Table>
              <thead><tr><Th>Cliente</Th><Th>NUIT</Th><Th>Contacto</Th><Th>Localidade</Th><Th>Condições</Th><Th /></tr></thead>
              <tbody>
                {list.data.items.map((c) => (
                  <Tr key={c.id} onClick={() => nav(`/clientes/${c.id}`)}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-500/10 text-brand-700 dark:text-brand-500">{c.customer_type === 'individual' ? <User className="size-4" /> : <Building2 className="size-4" />}</div>
                        <div className="min-w-0"><div className="truncate font-medium">{c.name}</div><div className="text-xs text-[var(--muted)]">{c.code}{c.trade_name ? ` · ${c.trade_name}` : ''}</div></div>
                        {c.is_final_consumer && <Badge>Consumidor final</Badge>}{!c.active && <Badge tone="red">Inactivo</Badge>}
                      </div>
                    </Td>
                    <Td className="tabular-nums">{c.nuit ?? (c.is_final_consumer ? '—' : <span className="text-amber-700 dark:text-amber-400">em falta</span>)}</Td>
                    <Td className="text-[var(--text-2)]"><div>{c.contact_name ?? ''}</div><div className="text-xs text-[var(--muted)]">{c.mobile ?? c.phone ?? c.email ?? ''}</div></Td>
                    <Td>{c.city}</Td>
                    <Td className="text-xs text-[var(--muted)]">{c.payment_terms_days != null ? `${c.payment_terms_days} dias` : 'Padrão'}{c.credit_limit ? ` · limite ${mt(c.credit_limit)}` : ''}</Td>
                    <Td className="text-right">{c.tags?.slice(0, 2).map((t: string) => <Badge key={t} tone="teal">{t}</Badge>)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title="Novo cliente" subtitle="Só o nome é obrigatório; o NUIT é exigido para emitir Facturas a empresas." size="lg" icon={<UserPlus />}>
        <CustomerForm onCancel={() => setCreating(false)} onSaved={(c) => { setCreating(false); qc.invalidateQueries({ queryKey: ['customers'] }); nav(`/clientes/${c.id}`); }} />
      </Modal>
    </>
  );
}

type DetailTab = 'resumo' | 'extracto' | 'documentos';

export function CustomerPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState<DetailTab>(sp.get('tab') === 'extracto' ? 'extracto' : 'resumo');
  const [sending, setSending] = useState(false);
  const pdf = async (kind: 'statement' | 'reminder') => {
    try { await download(`/accounts/statement/${id}/pdf?kind=${kind}&from=${range.from}&to=${range.to}`, undefined, true); } catch (e) { toast(errorText(e), 'error'); }
  };
  const comms = useQuery({ queryKey: ['communications', id], enabled: tab === 'extracto' && can('accounts:view'), queryFn: () => get(`/accounts/communications/${id}`) });
  const [range, setRange] = useState({ from: `${today().slice(0, 4)}-01-01`, to: today() });
  const c = useQuery({ queryKey: ['customer', id], queryFn: () => get(`/customers/${id}`) });
  const st = useQuery({ queryKey: ['statement', id, range], enabled: can('accounts:view') && tab !== 'documentos', queryFn: () => get(`/accounts/statement/${id}?from=${range.from}&to=${range.to}`) });
  const docs = useQuery({ queryKey: ['customer-docs', id], enabled: tab === 'documentos', queryFn: () => get(`/documents?customerId=${id}&pageSize=50`) });
  useEffect(() => { if (c.isError) nav('/clientes', { replace: true }); }, [c.isError, nav]);
  if (c.isLoading || !c.data) return <Loading />;
  const cu = c.data.customer;
  const remove = async () => {
    if (!(await confirm({ title: `Remover ${cu.name}?`, description: 'Se o cliente tiver documentos, fica apenas inactivo (os documentos continuam a referenciá-lo).', tone: 'danger', confirmLabel: 'Remover' }))) return;
    try {
      const r = await del(`/customers/${id}`);
      toast(r.deactivated ? 'Cliente desactivado' : 'Cliente removido');
      qc.invalidateQueries({ queryKey: ['customers'] });
      nav('/clientes');
    } catch (e) { toast(errorText(e), 'error'); }
  };

  return (
    <>
      <PageHeader eyebrow={<Link to="/clientes" className="hover:underline">Clientes</Link>}
        title={<span className="flex flex-wrap items-center gap-3">{cu.name} {!cu.active && <Badge tone="red">Inactivo</Badge>}</span>}
        subtitle={[cu.code, cu.nuit && `NUIT ${cu.nuit}`, cu.city].filter(Boolean).join(' · ')}
        actions={<>
          {can('documents:create') && <Link to={`/documentos/novo?tipo=FT&cliente=${id}`}><Button icon={<FilePlus2 className="size-4" />}>Nova factura</Button></Link>}
          {can('documents:create') && <Link to={`/documentos/novo?tipo=RE&cliente=${id}`}><Button variant="secondary" icon={<Receipt className="size-4" />}>Recebimento</Button></Link>}
          <Dropdown trigger={<Button variant="secondary" iconOnly icon={<MoreHorizontal className="size-4" />} aria-label="Mais acções" />} items={[
            { label: 'Editar ficha', icon: <Pencil />, onClick: () => setEditing(true), hidden: !can('customers:edit') || cu.is_final_consumer },
            { label: 'Nova pró-forma', icon: <FilePlus2 />, onClick: () => nav(`/documentos/novo?tipo=PF&cliente=${id}`), hidden: !can('documents:create') },
            'sep',
            { label: 'Remover cliente', icon: <Trash2 />, onClick: remove, danger: true, hidden: !can('customers:delete') || cu.is_final_consumer },
          ]} />
        </>} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Saldo em dívida" icon={<Wallet />} tone={Number(c.data.balance) > 0 ? undefined : 'good'} value={`${mt(c.data.balance)} MT`} />
        <StatCard label="Vendas totais (c/ IVA)" value={`${mt(c.data.stats.sales_total)} MT`} sub={`${c.data.stats.invoices} facturas`} />
        <StatCard label="Última compra" value={date(c.data.stats.last_purchase)} />
        <StatCard label="Limite de crédito" value={cu.credit_limit ? `${mt(cu.credit_limit)} MT` : 'Sem limite'} sub={cu.payment_terms_days != null ? `Prazo ${cu.payment_terms_days} dias` : 'Prazo padrão'} />
      </div>

      <Tabs className="mt-6" value={tab} onChange={setTab} tabs={[
        { id: 'resumo', label: 'Ficha' }, { id: 'extracto', label: 'Extracto de conta', hidden: !can('accounts:view') }, { id: 'documentos', label: 'Documentos' },
      ]} />
      <div className="mt-5">
        {tab === 'resumo' && (
          <div className="grid gap-6 lg:grid-cols-3">
            <Card title="Identificação e contactos" className="lg:col-span-2">
              <KeyValues items={[
                ['Tipo', cu.customer_type === 'individual' ? 'Particular' : 'Empresa / instituição'], ['Nome comercial', cu.trade_name],
                ['Email', cu.email && <a className="text-brand-700 hover:underline" href={`mailto:${cu.email}`}><Mail className="mr-1 inline size-3.5" />{cu.email}</a>],
                ['Email para documentos', cu.billing_email], ['Telefone', cu.phone && <><Phone className="mr-1 inline size-3.5" />{cu.phone}</>], ['Telemóvel', cu.mobile],
                ['WhatsApp', cu.whatsapp], ['Website', cu.website], ['Contacto', cu.contact_name && `${cu.contact_name}${cu.contact_role ? ` (${cu.contact_role})` : ''}`],
                ['Morada', [cu.address, cu.city, cu.province, cu.postal_code].filter(Boolean).join(', ')], ['Entrega', [cu.delivery_address, cu.delivery_city].filter(Boolean).join(', ')],
              ]} />
            </Card>
            <Card title="Condições">
              <KeyValues cols={1} items={[
                ['Prazo de pagamento', cu.payment_terms_days != null ? `${cu.payment_terms_days} dias` : 'Padrão da empresa'],
                ['Meio habitual', cu.default_payment_method && PAYMENT_METHODS[cu.default_payment_method as keyof typeof PAYMENT_METHODS]],
                ['Desconto habitual', cu.default_discount_pct && `${Number(cu.default_discount_pct)}%`],
                ['Dias de atraso tolerados', cu.credit_days_limit],
                ['Retenção na fonte', cu.withholding_pct && `${Number(cu.withholding_pct)}% ${cu.withholding_type ?? ''}`],
                ['Entidade pública', cu.is_public_entity ? 'Sim' : null],
                ['Etiquetas', cu.tags?.length ? <span className="flex flex-wrap gap-1">{cu.tags.map((t: string) => <Badge key={t} tone="teal">{t}</Badge>)}</span> : null],
                ['Notas', cu.notes && <span className="font-normal whitespace-pre-line">{cu.notes}</span>],
              ]} />
            </Card>
          </div>
        )}
        {tab === 'extracto' && (
          <Card padded={false}>
            <Toolbar>
              <span className="text-sm text-[var(--muted)]">Período</span>
              <Input type="date" className="h-8 w-40" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value || monthStart() })} aria-label="Desde" />
              <Input type="date" className="h-8 w-40" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value || today() })} aria-label="Até" />
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" icon={<FileDown className="size-4" />} onClick={() => pdf('statement')}>Extracto em PDF</Button>
                <Button size="sm" variant="secondary" icon={<FileDown className="size-4" />} onClick={() => pdf('reminder')}>Aviso de facturas em aberto</Button>
                {can('documents:send') && <Button size="sm" icon={<Mail className="size-4" />} onClick={() => setSending(true)}>Enviar por email</Button>}
              </div>
            </Toolbar>
            {st.isLoading || !st.data ? <div className="p-5"><Loading /></div> : (
              <Table>
                <thead><tr><Th>Data</Th><Th>Movimento</Th><Th>Vencimento</Th><Th className="text-right">Débito</Th><Th className="text-right">Crédito</Th><Th className="text-right">Saldo</Th></tr></thead>
                <tbody>
                  <tr className="bg-[var(--surface-2)]/50"><Td colSpan={5} className="text-[var(--muted)]">Saldo anterior a {date(st.data.from)}</Td><Td className="num font-medium">{mt(st.data.opening)}</Td></tr>
                  {st.data.rows.map((r: any) => (
                    <Tr key={r.id} onClick={r.document_id ? () => nav(`/documentos/${r.document_id}`) : undefined}>
                      <Td>{date(r.movement_date)}</Td><Td>{r.description}</Td><Td className="text-[var(--muted)]">{r.doc_type === 'FT' ? date(r.due_date) : ''}</Td>
                      <Td className="num">{r.kind === 'debit' ? mt(r.amount) : ''}</Td><Td className="num">{r.kind === 'credit' ? mt(r.amount) : ''}</Td>
                      <Td className="num font-medium">{mt(r.balance)}</Td>
                    </Tr>
                  ))}
                  <tr className="font-semibold"><Td colSpan={5}>Saldo em {date(st.data.to)}</Td><Td className="num">{mt(st.data.closing)} MT</Td></tr>
                </tbody>
              </Table>
            )}
            {!!comms.data?.items.length && (
              <div className="border-t border-[var(--border)] px-5 py-4">
                <div className="mb-2 text-xs font-semibold tracking-wider text-[var(--muted)] uppercase">Enviados por email</div>
                <ul className="space-y-1 text-sm">{comms.data.items.map((m: any) => (
                  <li key={m.id} className="flex flex-wrap justify-between gap-2">
                    <span>{m.kind === 'statement' ? `Extracto ${date(m.period_from)} – ${date(m.period_to)}` : 'Aviso de facturas em aberto'} → {m.recipient}</span>
                    <span className="text-xs text-[var(--muted)]">{dateTime(m.created_at)} · {m.requested_by_name ?? (m.automatic ? 'Automático' : '—')} · {m.status === 'sent' ? <Badge tone="green">Enviado</Badge> : m.status === 'failed' ? <Badge tone="red">Falhou{m.error ? `: ${m.error}` : ''}</Badge> : <Badge>Na fila</Badge>}</span>
                  </li>
                ))}</ul>
              </div>
            )}
          </Card>
        )}
        {tab === 'documentos' && (
          <Card padded={false}>
            {docs.isLoading || !docs.data ? <div className="p-5"><Loading /></div> : !docs.data.items.length ? <Empty title="Sem documentos" /> : (
              <Table>
                <thead><tr><Th>Documento</Th><Th>Data</Th><Th>Estado</Th><Th className="text-right">Total</Th><Th className="text-right">Pendente</Th></tr></thead>
                <tbody>{docs.data.items.map((d: any) => (
                  <Tr key={d.id} onClick={() => nav(`/documentos/${d.id}`)}>
                    <Td><div className="font-medium">{d.full_number ?? 'Rascunho'}</div><div className="text-xs text-[var(--muted)]">{docLabel(d.doc_type)}</div></Td>
                    <Td>{date(d.issue_date ?? d.created_at)}</Td><Td><StatusBadge status={d.status} training={d.training} /></Td>
                    <Td className="num">{mt(d.gross_total)}</Td><Td className="num">{d.pending !== null && d.status === 'issued' ? mt(d.pending) : '—'}</Td>
                  </Tr>
                ))}</tbody>
              </Table>
            )}
          </Card>
        )}
      </div>

      <SendStatementModal open={sending} onClose={() => setSending(false)} customer={cu} range={range} onSent={() => qc.invalidateQueries({ queryKey: ['communications', id] })} />
      <Modal open={editing} onClose={() => setEditing(false)} title="Editar cliente" subtitle="As alterações não afectam documentos já emitidos — cada documento guarda os dados da altura." size="lg" icon={<Pencil />}>
        <CustomerForm initial={cu} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); qc.invalidateQueries({ queryKey: ['customer', id] }); qc.invalidateQueries({ queryKey: ['customers'] }); }} />
      </Modal>
    </>
  );
}

/** Envio do extracto de conta corrente ou do aviso de facturas em aberto, com o PDF anexado. */
function SendStatementModal({ open, onClose, customer, range, onSent }: { open: boolean; onClose: () => void; customer: C; range: { from: string; to: string }; onSent: () => void }) {
  const { session } = useAuth();
  const textLocked = !session?.tenant.nuit_verified_at; // a API recusa texto livre sem NUIT confirmado
  // A API junta sempre ao Cc o email da empresa e o de quem envia.
  const copies = [...new Set([session?.tenant.email, session?.user.email].filter(Boolean).map((x) => x!.toLowerCase()))];
  const toast = useToast();
  const [f, setF] = useState({ kind: 'statement' as 'statement' | 'reminder', recipient: '', cc: '', message: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ kind: 'statement', recipient: customer.billing_email ?? customer.email ?? '', cc: '', message: '' }); }, [open, customer]);
  const cc = emailListInput.safeParse(f.cc);
  const ccError = f.cc.trim() && !cc.success ? cc.error.issues[0]?.message : undefined;
  const send = async () => {
    setBusy(true);
    try {
      await post(`/accounts/statement/${customer.id}/send`, { kind: f.kind, from: range.from, to: range.to, recipient: f.recipient, cc: f.cc || null, message: f.message || null });
      toast(`${f.kind === 'statement' ? 'Extracto' : 'Aviso'} enviado para ${f.recipient}`); onSent(); onClose();
    } catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Enviar por email" subtitle={customer.name} icon={<Mail />}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} disabled={!f.recipient.includes('@') || !!ccError} icon={<Mail className="size-4" />} onClick={send}>Enviar</Button></>}>
      <div className="space-y-4">
        <Chips value={f.kind} onChange={(k) => setF({ ...f, kind: k })} options={[{ id: 'statement', label: 'Extracto de conta corrente' }, { id: 'reminder', label: 'Aviso de facturas em aberto' }]} />
        <p className="text-xs text-[var(--muted)]">{f.kind === 'statement' ? `Movimentos de ${date(range.from)} a ${date(range.to)}, saldo e documentos por pagar.` : 'Lista dos documentos por pagar hoje, com os dias em atraso e os dados bancários para pagamento.'} Segue em PDF anexado.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Para" required><Input type="email" value={f.recipient} onChange={(e) => setF({ ...f, recipient: e.target.value })} /></Field>
          <Field label="Cc" hint={`Vários: separe com ; ou ,${copies.length ? ` · recebem sempre cópia: ${copies.join(', ')}` : ''}`} error={ccError}><Input value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} /></Field>
        </div>
        <Field label="Mensagem" hint={textLocked ? 'O texto personalizado fica disponível depois de o Makila confirmar o NUIT da empresa' : 'Opcional — por omissão segue um texto padrão'}><Textarea rows={3} value={f.message} disabled={textLocked} onChange={(e) => setF({ ...f, message: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}
